import type { FeatureNode } from './featureTreeUtils';

/**
 * Toggle a feature in the selected set, handling root/child cascade logic.
 * - Root toggle: select/deselect root + all children
 * - Child toggle: auto-select parent when selecting a child
 */
export function toggleFeatureInSet(
  featureId: number,
  currentSet: Set<number>,
  tree: FeatureNode[],
): Set<number> {
  const next = new Set(currentSet);
  const treeNode = tree.find(n => n.root.id === featureId);

  if (treeNode) {
    // Root feature: toggle root + all children
    const allIds = [featureId, ...treeNode.children.map(c => c.id)];
    const allSelected = allIds.every(id => next.has(id));
    if (allSelected) {
      allIds.forEach(id => next.delete(id));
    } else {
      allIds.forEach(id => next.add(id));
    }
  } else {
    // Child feature
    if (next.has(featureId)) {
      next.delete(featureId);
    } else {
      next.add(featureId);
      // Auto-select parent
      const parentNode = tree.find(n => n.children.some(c => c.id === featureId));
      if (parentNode) next.add(parentNode.root.id);
    }
  }

  return next;
}

/**
 * Compute the checkbox tri-state for a root feature.
 */
export function getRootCheckState(
  rootId: number,
  childIds: number[],
  selectedIds: Set<number>,
): { checked: boolean; indeterminate: boolean } {
  const rootSelected = selectedIds.has(rootId);
  if (childIds.length === 0) return { checked: rootSelected, indeterminate: false };
  const selectedChildren = childIds.filter(id => selectedIds.has(id)).length;
  const allSelected = rootSelected && selectedChildren === childIds.length;
  const someSelected = rootSelected || selectedChildren > 0;
  return { checked: allSelected, indeterminate: someSelected && !allSelected };
}

/**
 * Get feature IDs that the user doesn't already have (to add).
 */
export function getFeaturesToAdd(
  selectedIds: number[],
  userFeatureIds: number[],
): number[] {
  const userSet = new Set(userFeatureIds);
  return selectedIds.filter(fid => !userSet.has(fid));
}

/**
 * Get feature IDs to remove, cascading children of any selected root features.
 */
export function getFeaturesToRemove(
  selectedIds: number[],
  userFeatureIds: number[],
  childIdsByRoot: Map<number, number[]>,
): number[] {
  const userSet = new Set(userFeatureIds);
  const toRemove = new Set(
    selectedIds.filter(fid => userSet.has(fid)),
  );

  // Cascade: if removing a root feature, also remove its children
  for (const fid of selectedIds) {
    const childIds = childIdsByRoot.get(fid) ?? [];
    childIds.forEach(cid => {
      if (userSet.has(cid)) {
        toRemove.add(cid);
      }
    });
  }

  return Array.from(toRemove);
}

/**
 * Check if a user has all features in the selected set.
 */
export function userHasAllFeatures(
  selectedIds: number[],
  userFeatureIds: number[],
): boolean {
  const userSet = new Set(userFeatureIds);
  return selectedIds.every(fid => userSet.has(fid));
}
